import { useEffect, useState, type FormEvent } from 'react';
import { Check, Clock3, Gavel, LockKeyhole, Plus, ShieldAlert, UserCheck, XCircle } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { Auction, AuctionRequest } from '../types';
import { currencyInputToCents, formatMoney, formatTime, relativeTime } from '../utils';
import { ActionSheet, Badge, Button, CollapsiblePanel, EmptyBlock, Fab, Field, LoadingBlock, PageHeader, Panel, Textarea, TextInput } from '../components/ui';

const auctionTone = { active: 'active', sold: 'good', unsold: 'muted', cancelled: 'danger' } as const;
const auctionLabel = { active: '进行中', sold: '已成交', unsold: '流拍', cancelled: '已取消' } as const;
const requestLabel: Record<AuctionRequest['status'], string> = {
  pending: '待审核',
  approved: '已通过',
  rejected: '未通过'
};
const requestTone: Record<AuctionRequest['status'], 'warn' | 'good' | 'danger'> = {
  pending: 'warn',
  approved: 'good',
  rejected: 'danger'
};

export function AuctionsPage({ onNeedAuth }: { onNeedAuth: () => void }) {
  const { user, settings, permissions, refresh } = useAuth();
  const { toast, confirm } = useUi();
  const [items, setItems] = useState<Auction[] | null>(null);
  const [requests, setRequests] = useState<AuctionRequest[]>([]);
  const [pendingRequests, setPendingRequests] = useState<AuctionRequest[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bidInputs, setBidInputs] = useState<Record<string, string>>({});
  const [, setTick] = useState(0);

  const load = async () => {
    const myRequest = user
      ? api<{ requests: AuctionRequest[] }>('/api/auction-requests')
      : Promise.resolve({ requests: [] });
    const adminRequest = permissions.isStaff
      ? api<{ requests: AuctionRequest[] }>('/api/admin/auction-requests?status=pending')
      : Promise.resolve({ requests: [] });
    const [auctionData, requestData, adminData] = await Promise.all([
      api<{ auctions: Auction[]; tradeFeePercent: number }>('/api/auctions'),
      myRequest,
      adminRequest
    ]);
    setItems(auctionData.auctions);
    setRequests(requestData.requests);
    setPendingRequests(adminData.requests);
  };

  useEffect(() => {
    void load().catch(() => toast('拍卖数据加载失败', 'error'));
    const timer = window.setInterval(() => { setTick(value => value + 1); void load().catch(() => {}); }, 30_000);
    return () => window.clearInterval(timer);
  }, [user?.id, permissions.isStaff, settings.auctionRequireReview]);

  useEffect(() => {
    const timer = window.setInterval(() => setTick(value => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const canBid = Boolean(user?.boundToGame);
  const canSubmitAuction = canBid;
  const pendingMine = requests.filter(request => request.status === 'pending');
  const rejectedMine = requests.filter(request => request.status === 'rejected');

  const needBinding = () => {
    if (!user) { onNeedAuth(); return true; }
    if (!user.boundToGame) { toast('请先绑定游戏角色后再使用拍卖功能', 'error'); return true; }
    return false;
  };

  const openForm = () => {
    if (needBinding()) return;
    setFormOpen(true);
  };

  const reviewRequest = async (request: AuctionRequest, status: 'approved' | 'rejected') => {
    const approved = await confirm({
      title: status === 'approved' ? '通过拍卖申请' : '驳回拍卖申请',
      body: status === 'approved'
        ? `确认发布「${request.name}」？起拍价 ${formatMoney(request.startPriceCents, settings.currency)}，结束后按成交价结算。`
        : `确认驳回「${request.name}」？玩家可以修改拍卖内容后重新提交。`,
      danger: status === 'rejected',
      confirmText: status === 'approved' ? '通过并发布' : '确认驳回'
    });
    if (!approved) return;
    try {
      await api('/api/admin/auction-requests/' + request.id, {
        method: 'PATCH',
        body: JSON.stringify({ status, reviewNote: status === 'approved' ? '管理员审核通过，已正式发布' : '管理员审核驳回' })
      });
      await load();
      toast(status === 'approved' ? '拍卖已通过并发布' : '拍卖申请已驳回', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '处理失败', 'error');
    }
  };

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (needBinding()) return;
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const end = String(form.get('endAt') || '');
    const payload = { name: String(form.get('name') || ''), description: String(form.get('description') || ''), startPriceCents: currencyInputToCents(String(form.get('startPrice') || '0')), stepCents: currencyInputToCents(String(form.get('step') || '0')), endAt: new Date(end).getTime() };
    setBusy(true);
    try {
      const result = await api<{ mode: 'created' | 'pending' }>('/api/auction-requests', { method: 'POST', body: JSON.stringify(payload) });
      formEl.reset();
      setFormOpen(false);
      await load();
      toast(result.mode === 'pending' ? '拍卖申请已提交，等待管理员审核' : '拍卖已发起', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '提交失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const bid = async (item: Auction, event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (needBinding()) return;
    const amountCents = currencyInputToCents(bidInputs[item.id] || '0');
    try {
      await api('/api/auctions/' + item.id + '/bids', { method: 'POST', body: JSON.stringify({ amountCents }) });
      await Promise.all([load(), refresh()]);
      setBidInputs(current => ({ ...current, [item.id]: '' }));
      toast('出价成功', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '出价失败', 'error');
    }
  };

  const quickAdd = (item: Auction, minimum: number, amount: number) => {
    const current = Number(bidInputs[item.id] || minimum / 100) || minimum / 100;
    setBidInputs(inputs => ({ ...inputs, [item.id]: String(current + amount) }));
  };

  const cancel = async (item: Auction, staff = false) => {
    if (needBinding()) return;
    const approved = await confirm({ title: staff ? '管理员撤销拍卖' : '确认要撤拍？', body: staff ? '当前领先者的冻结资金会原路退回，操作将写入审计日志。' : '拍卖将立即结束，当前领先者资金会退回。', danger: true, confirmText: '确认撤销' });
    if (!approved) return;
    try {
      await api('/api/auctions/' + item.id + '/cancel', { method: 'POST' });
      await Promise.all([load(), refresh()]);
      toast('拍卖已撤销', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '撤销失败', 'error');
    }
  };

  const liveItems = items?.filter(item => item.status === 'active' && item.endAt > Date.now()) || [];
  const archivedItems = items?.filter(item => item.status !== 'active' || item.endAt <= Date.now()) || [];
  const requiresReview = settings.auctionRequireReview;

  const renderAuctionCard = (item: Auction, archived = false) => {
    const active = !archived && item.status === 'active' && item.endAt > Date.now();
    const minimum = item.topBidderId ? item.topPriceCents + item.stepCents : item.startPriceCents;
    const mine = user?.id === item.sellerId;
    const displayLabel = item.status === 'active' && !active ? '已结束' : auctionLabel[item.status];
    const displayTone = active ? 'active' : item.status === 'sold' ? 'good' : item.status === 'cancelled' ? 'danger' : 'muted';
    return (
      <article className={'record-card auction-card' + (archived ? ' archived' : ' live')} key={item.id}>
        {active && mine && <button className="corner-action danger-text" onClick={() => void cancel(item)}><XCircle size={14} /> 撤拍</button>}
        <header><div><span className="record-type">{archived ? 'ARCHIVED AUCTION' : 'LIVE AUCTION'}</span><h3>{item.name}</h3><small>{item.sellerName} · {formatTime(item.createdAt)}</small></div><Badge tone={displayTone}>{displayLabel}</Badge></header>
        {item.description && <p className="record-description">{item.description}</p>}
        <div className="auction-metrics"><div><span>当前价格</span><strong>{formatMoney(item.topBidderId ? item.topPriceCents : item.startPriceCents, settings.currency)}</strong></div><div><span>领先者</span><strong>{item.topBidderName || '暂无'}</strong></div><div><span>加价幅度</span><strong>{formatMoney(item.stepCents, settings.currency)}</strong></div><div><span>{active ? '剩余时间' : '结束时间'}</span><strong><Clock3 size={14} /> {active ? relativeTime(item.endAt) : formatTime(item.endAt)}</strong></div></div>
        {item.status === 'sold' && item.feeCents !== null && <p className="form-note">成交价 {formatMoney(item.topPriceCents, settings.currency)} · 手续费 {formatMoney(item.feeCents || 0, settings.currency)} · 卖家实收 {formatMoney(item.sellerNetCents || 0, settings.currency)}</p>}
        {active && !mine && canBid && <form className="bid-form" onSubmit={event => void bid(item, event)}><TextInput name="amount" type="number" min={minimum / 100} step="0.01" value={bidInputs[item.id] || ''} onChange={event => setBidInputs(inputs => ({ ...inputs, [item.id]: event.target.value }))} placeholder={'最低 ' + formatMoney(minimum, settings.currency)} required /><Button variant="primary" type="submit"><Gavel size={16} /> 出价</Button></form>}
        {active && !mine && canBid && <div className="quick-bids"><span>快捷加价</span>{[10, 50, 100].map(amount => <button type="button" key={amount} onClick={() => quickAdd(item, minimum, amount)}>+{amount}</button>)}</div>}
        {active && !mine && !canBid && <p className="form-note"><LockKeyhole size={14} /> 登录并绑定游戏角色后即可出价，出价无需审核。</p>}
        {active && mine && <p className="form-note">这是你发起的拍卖，不能参与出价。</p>}
        {item.bids.length > 0 && <details className="bid-history"><summary>出价记录（{item.bids.length}）</summary>{item.bids.slice(0, 8).map(log => <div key={log.id}><span>{log.bidderName}</span><b>{formatMoney(log.amountCents, settings.currency)}</b><small>{formatTime(log.createdAt)}</small></div>)}</details>}
        {active && permissions.isStaff && !mine && <footer className="record-actions"><Button variant="danger" onClick={() => void cancel(item, true)}><ShieldAlert size={16} /> 管理撤销</Button></footer>}
      </article>
    );
  };

  return (
    <>
      <PageHeader title="法拍场" description={`出价最高者成交；领先出价资金会被冻结，被超过后自动退回。物品成交收取 ${settings.tradeFeePercent}% 手续费。`} action={<a className="button ghost" href="#/tutorials">教学视频</a>} />

      <Panel title="发布拍卖" eyebrow="AUCTION PUBLISHING" action={<Badge tone={requiresReview ? 'warn' : 'good'}>{requiresReview ? '每次发布需审核' : '可直接发布'}</Badge>}>
        <p className="muted">{requiresReview ? '当前开启发起拍卖审核：每次提交拍卖都会生成一条独立申请，管理员通过后才会正式发布。参与出价始终不需要审核。' : '当前未开启发起拍卖审核：绑定游戏角色后可以直接发布拍卖。参与出价同样不需要审核。'}</p>
        {user && user.boundToGame && pendingMine.length > 0 && <p className="form-note">你有 {pendingMine.length} 条拍卖申请等待审核。</p>}
        {user && user.boundToGame && pendingMine.length === 0 && rejectedMine.length > 0 && <p className="form-note warn-text">最近有 {rejectedMine.length} 条拍卖申请未通过，可以修改内容后重新提交。</p>}
        <div className="inline-actions top-gap">
          {canSubmitAuction && <Button variant="primary" onClick={openForm}><Plus size={16} />{requiresReview ? '提交拍卖审核' : '发起拍卖'}</Button>}
          {!user && <Button variant="primary" onClick={onNeedAuth}>登录 / 注册</Button>}
          {user && !user.boundToGame && <a className="button ghost" href="#/profile">去绑定游戏角色</a>}
        </div>
      </Panel>

      {user && requiresReview && requests.length > 0 && (
        <CollapsiblePanel title={'我的拍卖申请 · ' + requests.length} eyebrow="MY AUCTION REQUESTS" className="top-gap" defaultOpen={pendingMine.length > 0}>
          <div className="compact-list">
            {requests.map(request => (
              <div className="compact-row" key={request.id}>
                <div>
                  <strong>{request.name}</strong>
                  <small>起拍 {formatMoney(request.startPriceCents, settings.currency)} · 结束 {formatTime(request.endAt)} · 提交 {formatTime(request.createdAt)}</small>
                  {request.reviewNote && <small>审核备注：{request.reviewNote}</small>}
                </div>
                <Badge tone={requestTone[request.status]}>{requestLabel[request.status]}</Badge>
              </div>
            ))}
          </div>
        </CollapsiblePanel>
      )}

      {permissions.isStaff && (
        <Panel title={'待审核拍卖申请' + (pendingRequests.length ? ' · ' + pendingRequests.length : '')} eyebrow="AUCTION REQUESTS" className="top-gap auction-application-panel" action={pendingRequests.length > 0 ? <Badge tone="warn">待处理 {pendingRequests.length}</Badge> : undefined}>
          {pendingRequests.length === 0 ? <EmptyBlock title="暂无待审核拍卖申请" /> : (
            <div className="auction-application-list">
              {pendingRequests.map(request => (
                <article className="auction-application-card" key={request.id}>
                  <header className="auction-application-head">
                    <div className="auction-application-identity">
                      <span className="auction-application-avatar"><UserCheck size={18} /></span>
                      <div><strong>{request.name}</strong><span>{request.displayName || request.sellerName} · @{request.username}</span></div>
                    </div>
                    <span className="auction-application-time"><Clock3 size={14} />{formatTime(request.createdAt)}</span>
                  </header>
                  <div className="auction-application-reason">
                    <span>拍品说明</span>
                    <p>{request.description || '未填写拍品说明'}</p>
                  </div>
                  <div className="record-meta">
                    <span>起拍价 <b>{formatMoney(request.startPriceCents, settings.currency)}</b></span>
                    <span>最低加价 <b>{formatMoney(request.stepCents, settings.currency)}</b></span>
                    <span>计划结束 <b>{formatTime(request.endAt)}</b></span>
                  </div>
                  <footer className="auction-application-actions">
                    <small>通过后会立即创建并公开展示这场拍卖；出价仍对所有绑定玩家开放。</small>
                    <div>
                      <Button variant="danger" onClick={() => void reviewRequest(request, 'rejected')}>驳回</Button>
                      <Button variant="primary" onClick={() => void reviewRequest(request, 'approved')}><Check size={15} />通过并发布</Button>
                    </div>
                  </footer>
                </article>
              ))}
            </div>
          )}
        </Panel>
      )}

      <ActionSheet open={formOpen} onClose={() => setFormOpen(false)} title={requiresReview ? '申请发起拍卖' : '发起拍卖'} eyebrow={requiresReview ? 'AUCTION REQUEST' : 'NEW AUCTION'} description={requiresReview ? `提交后需要管理员单独审核，通过后才会正式发布。成交后扣除 ${settings.tradeFeePercent}% 手续费。` : `发布后立即开始展示。成交后扣除 ${settings.tradeFeePercent}% 手续费。`}>
        <form className="stack-form" onSubmit={create}>
          {requiresReview && <p className="form-note">每次拍卖都会单独审核；本次审核不会影响以后的新拍卖，也不影响参与出价。</p>}
          <Field label="拍品名称"><TextInput name="name" maxLength={60} required placeholder="例：下界合金剑" /></Field>
          <Field label="说明"><Textarea name="description" rows={4} maxLength={600} placeholder="拍品属性、数量、交付方式" /></Field>
          <div className="form-grid"><Field label={'起拍价（' + settings.currency + '）'}><TextInput name="startPrice" type="number" min="0.01" step="0.01" required /></Field><Field label={'最低加价（' + settings.currency + '）'}><TextInput name="step" type="number" min="0.01" step="0.01" defaultValue="1" required /></Field></div>
          <Field label="结束时间"><TextInput name="endAt" type="datetime-local" required /></Field>
          <Button variant="primary" type="submit" loading={busy} className="wide"><Plus size={17} />{requiresReview ? '提交拍卖审核' : '发起拍卖'}</Button>
        </form>
      </ActionSheet>

      <Panel title={'正在进行中的拍卖' + (items ? ' · ' + liveItems.length : '')} eyebrow="LIVE AUCTION" className="auction-live-panel top-gap" action={<Badge tone="active">优先展示</Badge>}>
        {!items ? <LoadingBlock /> : liveItems.length === 0 ? <EmptyBlock title="当前没有正在进行的拍卖" description={canSubmitAuction ? (requiresReview ? '可以提交一场拍卖申请，管理员通过后会显示在这里。' : '可以发起一场新的拍卖，拍品会显示在这里。') : '登录并绑定游戏角色后即可参与出价或提交拍卖。'} action={canSubmitAuction ? <Button variant="primary" onClick={openForm}><Gavel size={16} />{requiresReview ? '提交拍卖审核' : '发起拍卖'}</Button> : !user ? <Button variant="primary" onClick={onNeedAuth}>登录 / 注册</Button> : undefined} /> : (
          <div className="auction-live-grid">{liveItems.map(item => renderAuctionCard(item))}</div>
        )}
      </Panel>

      {archivedItems.length > 0 && (
        <CollapsiblePanel title={'已结束 / 不可用 · ' + archivedItems.length} eyebrow="AUCTION ARCHIVE" className="auction-archive-panel top-gap" defaultOpen={false} action={<span className="muted small">已取消、已成交、流拍和过期拍卖</span>}>
          <div className="auction-archive-grid">{archivedItems.map(item => renderAuctionCard(item, true))}</div>
        </CollapsiblePanel>
      )}
      {canSubmitAuction && <Fab label={requiresReview ? '申请拍卖' : '发起拍卖'} onClick={openForm} />}
    </>
  );
}